import React, { FC, useMemo } from 'react';
import { List } from '@mui/material';
import { OPM_CALCULATOR_ENABLER } from 'common/constants/features';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { PUBLIC_COMPANIES } from 'pages/Valuations/util/constants';
import { EQUITY_ALLOCATION_SCENARIO } from 'pages/ValuationsAllocation/common/constants/equityAllocation';
import { OPM_CALCULATOR_MODEL_NAME } from 'pages/ValuationsAllocation/common/constants/valuations';
import { ApproachScenarioTypeOption, ApproachTypes } from 'pages/ValuationsAllocation/components';
import { StyledDialog } from './styles';
import { NewScenarioApproachDialogProps } from './types';

// updating here
const NewScenarioApproachDialog: FC<NewScenarioApproachDialogProps> = ({
  approachTypes,
  disableAddScenarioButton,
  handleApproachTypeClick,
  handleScenarioTypeClick,
  initialSelection,
  onDialogClose,
  openDialog,
  approaches,
  hasOPMConsumer,
}: NewScenarioApproachDialogProps) => {
  const hasGPC = approaches.some(approach => approach?.approach_type === PUBLIC_COMPANIES);

  const [, checkFeature] = useCheckFeatures();

  // Hidden rather than disabled while unreleased: a disabled row advertises a feature nobody can
  // turn on. Flags hydrate asynchronously, so the option can appear a moment after the dialog
  // opens; that is fine here because nothing irreversible depends on it.
  const isOpmCalculatorFeatureEnabled = checkFeature(OPM_CALCULATOR_ENABLER);
  const visibleApproachTypes = useMemo(
    () =>
      approachTypes.filter(
        approach => approach !== OPM_CALCULATOR_MODEL_NAME || isOpmCalculatorFeatureEnabled
      ),
    [approachTypes, isOpmCalculatorFeatureEnabled]
  );

  // Disabled, not hidden, until something on the valuation prices with OPM: on a clean valuation
  // the calculator has nothing to reproduce, but the row tells the user it exists.
  const getIsApproachDisabled = (approach: ApproachTypes) => {
    if (approach === 'Calibration') {
      return !hasGPC;
    }
    if (approach === OPM_CALCULATOR_MODEL_NAME) {
      return !hasOPMConsumer;
    }
    return false;
  };

  return (
    <StyledDialog onClose={onDialogClose} open={openDialog}>
      <List>
        {/* Approaches */}
        {visibleApproachTypes.map(approach => (
          <ApproachScenarioTypeOption
            approach={approach}
            autoFocus={initialSelection === approach}
            onClick={handleApproachTypeClick}
            key={approach}
            isDisabled={getIsApproachDisabled(approach)}
          />
        ))}

        {/* Allocation Scenario */}
        <ApproachScenarioTypeOption
          approach={EQUITY_ALLOCATION_SCENARIO as ApproachTypes}
          autoFocus={initialSelection === EQUITY_ALLOCATION_SCENARIO}
          isDisabled={disableAddScenarioButton}
          key={EQUITY_ALLOCATION_SCENARIO}
          onClick={handleScenarioTypeClick}
        />
      </List>
    </StyledDialog>
  );
};

export default NewScenarioApproachDialog;
